import { useState, useEffect, useCallback } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  FilterX,
  Edit,
  FolderPlus,
  Building2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Trash2,
  ClipboardList,
  ScrollText,
  Settings2,
  ShoppingCart,
  Ban,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../core/apiClient';
import { ExcelFilterDropdown } from '../components/ui/excel-filter';
import BudgetList from './crm/BudgetList';
import Licitacions from './Licitacions';
import SalesOrderList from './crm/SalesOrderList';
import ExpedientComercialList from './crm/ExpedientComercialList';

// Types
interface Client {
  id: string;
  code: string;
  name: string;
  tradeName?: string;
  cif: string;
  // Codi del catàleg /catalog/client-types (Activitat de client), no un enum fix.
  type: string;
  city?: string;
  phone?: string;
  email?: string;
  creditLimit: number;
  currentBalance: number;
  contactName?: string;
  naturalesaClientId?: number;
  isActive: number;
  brand?: {
    id: string;
    code: string;
    name: string;
    primaryColor?: string;
  };
}


type TabValue = 'clients' | 'pressupostos' | 'licitacions' | 'comandes' | 'expedients';

const CARDS = [
  { id: 'clients'      as TabValue, label: 'Clients',          icon: Building2,    color: 'blue' },
  { id: 'licitacions'  as TabValue, label: 'Licitacions',      icon: ScrollText,   color: 'amber' },
  { id: 'expedients'   as TabValue, label: 'Expedients', icon: FolderPlus, color: 'teal' },
  { id: 'pressupostos' as TabValue, label: 'Pressupostos',     icon: ClipboardList, color: 'purple' },
  // Comandes client: amagat temporalment a demanda del client (mòdul es manté al codi, nomes es treu de la pestanya).
  // { id: 'comandes'  as TabValue, label: 'Comandes client',  icon: ShoppingCart, color: 'green' },
];

const COLOR: Record<string, { border: string; bg: string; icon: string; iconBg: string }> = {
  blue:   { border: 'border-blue-500',   bg: 'bg-blue-50/50',   icon: 'text-blue-500',   iconBg: 'bg-blue-500/10' },
  purple: { border: 'border-purple-500', bg: 'bg-purple-50/50', icon: 'text-purple-500', iconBg: 'bg-purple-500/10' },
  amber:  { border: 'border-amber-500',  bg: 'bg-amber-50/50',  icon: 'text-amber-500',  iconBg: 'bg-amber-500/10' },
  green:  { border: 'border-green-500',  bg: 'bg-green-50/50',  icon: 'text-green-500',  iconBg: 'bg-green-500/10' },
  teal:   { border: 'border-teal-500',   bg: 'bg-teal-50/50',   icon: 'text-teal-500',   iconBg: 'bg-teal-500/10' },
};

export default function CRM() {
  const navigate = useNavigate();

  // Tab state — sessionStorage (no localStorage): la primera vegada que
  // s'entra a CRM en una sessió sempre mostra Expedients Comercials.
  const [activeTab, setActiveTab] = useSessionState<TabValue>('crm:activeTab', 'expedients');

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Chivato: círcol vermell a la pestanya "Licitacions" quan hi ha candidates
  // (de la ingesta automàtica) pendents de revisar.
  const [candidatesCount, setCandidatesCount] = useState(0);
  useEffect(() => {
    api.get<{ success: boolean; pagination: { total: number } }>('/tenders', { status: 'CANDIDATA', limit: 1 })
      .then((r) => { if (r.success) setCandidatesCount(r.pagination.total); })
      .catch(() => {});
  }, []);

  // Marcas dinàmiques per al filtre
  const [brands, setBrands] = useState<{ id: string; code: string; name: string }[]>([]);
  useEffect(() => {
    api.get<{ success: boolean; data: { id: string; code: string; name: string }[] }>('/brands')
      .then(r => { if (r.success) setBrands(r.data); })
      .catch(() => {});
  }, []);

  // Activitats de client (catàleg) per al filtre
  const [clientTypes, setClientTypes] = useState<{ id: number; code: string; name: string; isActive: number }[]>([]);
  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/catalog/client-types')
      .then(r => { if (r.success) setClientTypes((r.data ?? []).filter(t => t.isActive)); })
      .catch(() => {});
  }, []);

  // Naturalesa de client (catàleg) per a la columna i el filtre
  const [naturalesaClients, setNaturalesaClients] = useState<{ id: number; code: string; name: string; isActive: number }[]>([]);
  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/catalog/naturalesa-client')
      .then(r => { if (r.success) setNaturalesaClients((r.data ?? []).filter(t => t.isActive)); })
      .catch(() => {});
  }, []);

  // Rol de client (catàleg) per al filtre
  const [rolClients, setRolClients] = useState<{ id: number; code: string; name: string; isActive: number }[]>([]);
  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/catalog/rol-client')
      .then(r => { if (r.success) setRolClients((r.data ?? []).filter(t => t.isActive)); })
      .catch(() => {});
  }, []);

  // Estat comercial (catàleg) per al filtre
  const [estatsComercials, setEstatsComercials] = useState<{ id: number; code: string; name: string; isActive: number }[]>([]);
  useEffect(() => {
    api.get<{ success: boolean; data: { id: number; code: string; name: string; isActive: number }[] }>('/catalog/estats-comercials')
      .then(r => { if (r.success) setEstatsComercials((r.data ?? []).filter(t => t.isActive)); })
      .catch(() => {});
  }, []);

  // Filtres
  const [search, setSearch] = useSessionState('crm:search', '');
  const [brandFilter, setBrandFilter] = useSessionState('crm:brandFilter', 'all');
  const [typeFilter, setTypeFilter] = useSessionState('crm:typeFilter', 'all');
  const [naturalesaFilter, setNaturalesaFilter] = useSessionState('crm:naturalesaFilter', 'all');
  const [rolFilter, setRolFilter] = useSessionState('crm:rolFilter', 'all');
  const [estatFilter, setEstatFilter] = useSessionState('crm:estatFilter', 'all');

  const handleClearFilters = () => {
    setSearch('');
    setBrandFilter('all');
    setTypeFilter('all');
    setNaturalesaFilter('all');
    setRolFilter('all');
    setEstatFilter('all');
  };

  // Claus noves (v2) perque un valor antic 'crm:sortOrder'='ASC' guardat a
  // sessionStorage abans d'aquest canvi no tapi el nou valor per defecte.
  const [sortBy, setSortBy] = useSessionState<'code' | 'name'>('crm:sortBy:v2', 'code');
  const [sortOrder, setSortOrder] = useSessionState<'ASC' | 'DESC'>('crm:sortOrder:v2', 'DESC');

  const handleSort = (field: 'code' | 'name') => {
    if (sortBy === field) {
      setSortOrder((s) => (s === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
  };

  // Paginació
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const handleTabChange = (tab: TabValue) => {
    setActiveTab(tab);
  };

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        sortBy,
        sortOrder,
      });

      if (search) params.append('search', search);
      if (brandFilter !== 'all') params.append('brandId', brandFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (naturalesaFilter !== 'all') params.append('naturalesaClientId', naturalesaFilter);
      if (rolFilter !== 'all') params.append('rolClientId', rolFilter);
      if (estatFilter !== 'all') params.append('estatComercialId', estatFilter);

      const response = await api.get<{
        success: boolean;
        data: Client[];
        pagination: { total: number; page: number; limit: number; totalPages: number };
        stats: { activeClients: number; atRiskClients: number; blockedClients: number };
      }>(`/clients?${params}`);

      setClients(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);

    } catch (err) {
      console.error('[v0] Error fetching clients:', err);
      setError('Error en carregar els clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [search, brandFilter, typeFilter, naturalesaFilter, rolFilter, estatFilter, page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Resetejar pagina quan canvien els filtres, l'ordenació o la mida de pàgina
  useEffect(() => {
    setPage(1);
  }, [search, brandFilter, typeFilter, naturalesaFilter, rolFilter, estatFilter, sortBy, sortOrder, pageSize]);

  const handleEditClient = (clientId: string) => {
    navigate(`/crm/clients/${clientId}/edit`);
  };

  const handleCreateProject = (clientId: string) => {
    navigate(`/crm/expedients-comercials/new?clientId=${clientId}`);
  };

  const handleDeleteClient = async (client: Client) => {
    if (!window.confirm(`Segur que vols eliminar el client "${client.name}"?`)) {
      return;
    }
    try {
      await api.delete(`/clients/${client.id}`);
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Error en eliminar el client');
    }
  };

  const handleNewClient = () => {
    navigate('/crm/clients/new');
  };

  return (
    <div className="space-y-6">
      {/* Capcalera */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">CRM</h1>
          <p className="text-muted-foreground">
            Gestió de clients i relacions comercials
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'clients' && (
            <Button size="sm" onClick={handleNewClient}>
              <Plus className="h-4 w-4 mr-2" />
              Nou Client
            </Button>
          )}
          {activeTab === 'pressupostos' && (
            <Button size="sm" onClick={() => navigate('/crm/pressupostos/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Nou Pressupost
            </Button>
          )}
          {activeTab === 'licitacions' && (
            <>
              <Button size="sm" variant="outline" onClick={() => navigate('/crm/licitacions/criteris')}>
                <Settings2 className="h-4 w-4 mr-2" />
                Criteris de valoració
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate('/crm/licitacions/estadistiques-eliminacio')}>
                <Ban className="h-4 w-4 mr-2" />
                Estadística criteris d'eliminació
              </Button>
              <Button size="sm" onClick={() => navigate('/crm/licitacions/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Licitació
              </Button>
            </>
          )}
          {activeTab === 'comandes' && (
            <Button size="sm" onClick={() => navigate('/crm/comandes/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Comanda
            </Button>
          )}
          {activeTab === 'expedients' && (
            <Button size="sm" onClick={() => navigate('/crm/expedients-comercials/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Crear Expedient Comercial
            </Button>
          )}
        </div>
      </div>

      {/* Pestanyes com a tarjetes */}
      <div className="grid grid-cols-5 gap-3">
        {CARDS.map(({ id, label, icon: Icon, color }) => {
          const c = COLOR[color];
          const active = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={`relative flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${
                active ? `${c.border} ${c.bg}` : 'border-border bg-white hover:shadow-sm'
              }`}
            >
              {id === 'licitacions' && candidatesCount > 0 && (
                <span
                  className="absolute -top-1.5 -right-1.5 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white"
                  title={`${candidatesCount} candidata${candidatesCount === 1 ? '' : 's'} pendent${candidatesCount === 1 ? '' : 's'} de revisar`}
                />
              )}
              <div className={`p-1.5 rounded-lg shrink-0 ${c.iconBg}`}>
                <Icon className={`h-4 w-4 ${c.icon}`} />
              </div>
              <span className="text-xs font-semibold">{label}</span>
            </button>
          );
        })}
      </div>

      {/* Contingut per pestanya */}
      {activeTab === 'clients' && (
        <>
          {/* Filtres */}
          <Card className="rounded-xl">
            <CardContent className="pt-4">
              <div className="flex flex-col md:flex-row gap-3">
                {/* Netejar filtres */}
                <button
                  type="button"
                  onClick={handleClearFilters}
                  title="Netejar tots els filtres"
                  className="flex items-center justify-center px-2.5 border rounded-lg hover:bg-muted transition-colors shrink-0"
                >
                  <FilterX className="h-4 w-4 text-muted-foreground" />
                </button>

                {/* Cercador */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Cercar per nom, CIF o codi..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>

                {/* Filtre per marca */}
                <div className="flex items-center gap-2">
                  <ExcelFilterDropdown
                    label="Marca"
                    options={brands.map(b => ({ value: String(b.id), label: b.name }))}
                    selected={brandFilter === 'all' ? [] : brandFilter.split(',')}
                    onApply={(values) => setBrandFilter(values.length === 0 ? 'all' : values.join(','))}
                  />
                </div>

                {/* Filtre per naturalesa */}
                <ExcelFilterDropdown
                  label="Naturalesa"
                  options={naturalesaClients.map(nc => ({ value: String(nc.id), label: nc.name }))}
                  selected={naturalesaFilter === 'all' ? [] : naturalesaFilter.split(',')}
                  onApply={(values) => setNaturalesaFilter(values.length === 0 ? 'all' : values.join(','))}
                />

                {/* Filtre per activitat */}
                <ExcelFilterDropdown
                  label="Activitat"
                  options={clientTypes.map(ct => ({ value: ct.code, label: ct.name }))}
                  selected={typeFilter === 'all' ? [] : typeFilter.split(',')}
                  onApply={(values) => setTypeFilter(values.length === 0 ? 'all' : values.join(','))}
                />

                {/* Filtre per rol */}
                <ExcelFilterDropdown
                  label="Rol"
                  options={rolClients.map(rc => ({ value: String(rc.id), label: rc.name }))}
                  selected={rolFilter === 'all' ? [] : rolFilter.split(',')}
                  onApply={(values) => setRolFilter(values.length === 0 ? 'all' : values.join(','))}
                />

                {/* Filtre per estat comercial */}
                <ExcelFilterDropdown
                  label="Estat"
                  options={estatsComercials.map(ec => ({ value: String(ec.id), label: ec.name }))}
                  selected={estatFilter === 'all' ? [] : estatFilter.split(',')}
                  onApply={(values) => setEstatFilter(values.length === 0 ? 'all' : values.join(','))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Taula de clients */}
          <Card className="rounded-xl">
            <CardHeader className="pb-3 border-b">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Clients
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
                  <p className="text-sm font-medium">{error}</p>
                  <button
                    onClick={fetchClients}
                    className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg"
                  >
                    Reintentar
                  </button>
                </div>
              ) : loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : clients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-sm font-medium text-muted-foreground">
                    No s&apos;han trobat clients
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Prova de canviar els filtres o crear un nou client
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">
                          <button
                            type="button"
                            onClick={() => handleSort('code')}
                            className="flex items-center gap-1 hover:text-foreground transition-colors"
                          >
                            Codi
                            {sortBy === 'code' && (sortOrder === 'ASC' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            onClick={() => handleSort('name')}
                            className="flex items-center gap-1 hover:text-foreground transition-colors"
                          >
                            Client
                            {sortBy === 'name' && (sortOrder === 'ASC' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                          </button>
                        </TableHead>
                        <TableHead>Contacte</TableHead>
                        <TableHead>Activitat</TableHead>
                        <TableHead className="w-[120px]">Accions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.map((client) => {
                        const activitatLabel = clientTypes.find((ct) => ct.code === client.type)?.name ?? client.type;
                        return (
                          <TableRow
                            key={client.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleEditClient(client.id)}
                          >
                            <TableCell className="font-mono text-sm">
                              {client.code}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{client.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {client.cif}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {client.contactName || '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {activitatLabel}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => handleEditClient(client.id)}
                                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleCreateProject(client.id)}
                                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                                  title="Crear Expedient Comercial"
                                >
                                  <FolderPlus className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClient(client)}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Paginació */}
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">{total} clients trobats</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Mostrar:</span>
                        {[25, 50, 100].map((size) => (
                          <button
                            key={size}
                            onClick={() => { setPageSize(size); setPage(1); }}
                            className={`px-2 py-0.5 text-xs rounded border transition-colors ${
                              pageSize === size ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                            }`}
                          >{size}</button>
                        ))}
                      </div>
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs">Pàgina {page} de {totalPages}</span>
                        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'pressupostos' && <BudgetList />}

      {activeTab === 'licitacions' && <Licitacions />}

      {activeTab === 'comandes' && <SalesOrderList />}

      {activeTab === 'expedients' && <ExpedientComercialList />}
    </div>
  );
}
